import validate from 'validate.js';
import moment from 'moment';
import { values, map, head, keys, pick } from 'lodash';

import { formatDate, convertDate } from '../../../../utility/dates';
import { isDateBefore, isDateDifferenceLessThan, isDateValid } from './validations';

validate.extend(validate.validators.datetime, {
  parse: value => {
    if (!isDateValid(value)) {
      return null;
    }

    return +convertDate(value, { keepLocalTime: true });
  },
  format: value => formatDate(value)
});

validate.validators.afterDate = (endDate, options, key, attributes) => {
  const dateToCompareAgainst = options.useDefaultDate
    ? convertDate(moment())
    : attributes[options.attributeName];

  if (isDateBefore(dateToCompareAgainst, endDate)) {
    return null;
  }

  return `cannot be before ${validate.prettify(options.attributeName)}`;
};

validate.validators.beforeDateLimit = (endDate, options, key, attributes) => {
  const dateToCompareAgainst = options.useDefaultDate
    ? convertDate(moment())
    : attributes[options.attributeName];

  if (isDateDifferenceLessThan(dateToCompareAgainst, endDate, options.amount, options.format)) {
    return null;
  }

  return `cannot be more than ${options.amount} ${options.format} after ${validate.prettify(
    options.attributeName
  )}`;
};

const buildConstraints = ({ useDefaultDate }) => ({
  startDate: {
    presence: {
      allowEmpty: false,
      message: 'is required'
    },
    datetime: {
      earliest: convertDate(moment()),
      tooEarly: 'must be after now'
    }
  },
  endDate: {
    presence: {
      allowEmpty: false,
      message: 'is required'
    },
    datetime: true,
    afterDate: {
      useDefaultDate,
      attributeName: 'startDate'
    },
    beforeDateLimit: {
      useDefaultDate,
      attributeName: 'startDate',
      amount: 24,
      format: 'hours'
    }
  },
  amount: {
    presence: {
      allowEmpty: false,
      message: 'is required'
    },
    numericality: {
      strict: true,
      greaterThanOrEqualTo: 1,
      lessThanOrEqualTo: 49
    }
  },
  states: {
    presence: {
      allowEmpty: false,
      message: '^Please select at least one state'
    }
  }
});

export const validator = (attributes, options = { useDefaultDate: false }) => {
  const fieldsToValidate = keys(attributes);

  const constraints = pick(buildConstraints(options), fieldsToValidate);

  return validate(attributes, constraints);
};

export const getFormErrors = (attributes, options) => {
  const errors = validator(attributes, options);

  return map(values(errors), head);
};
